"use client";

import dynamic from "next/dynamic";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { Container } from "@/components/shell/Container";
import type { OpenHouseMapItem } from "@/lib/data/open-houses";

const Explorer = dynamic(() => import("@/components/home/OpenHouseWeekExplorer")
  .then((module) => module.OpenHouseWeekExplorer), { ssr: false });

/** Homepage-only boundary. No listing records or explorer bundle in its SSR.
 * Dedicated /open-houses retains its existing server data and immediate map.
 */
export function OpenHouseExplorerDeferred({ basePath, id, orgIdentifier }: { basePath: string; id: string; orgIdentifier?: string }) {
  const container = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(false);
  const [items, setItems] = useState<OpenHouseMapItem[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") {
      setActive(true);
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        setActive(true);
        observer.disconnect();
      }
    }, { rootMargin: "300px 0px" });
    if (container.current) observer.observe(container.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!active) return;
    const controller = new AbortController();
    setFailed(false);
    async function load() {
      try {
        const response = await fetch("/api/open-houses", { signal: controller.signal, priority: "low" });
        if (!response.ok) throw new Error("Open houses unavailable");
        const data = await response.json();
        if (!Array.isArray(data.items)) throw new Error("Invalid open-house response");
        if (!controller.signal.aborted) setItems(data.items);
      } catch {
        if (!controller.signal.aborted) setFailed(true);
      }
    }
    void load();
    return () => controller.abort();
  }, [active, attempt]);

  return (
    <section id={id} className="scroll-mt-16" style={{ backgroundColor: "var(--brand-page-bg)" }}>
      <Container className="py-12 sm:py-16">
        <div ref={container} className="min-h-[904px]">
          {items !== null ? <Explorer items={items} basePath={basePath} deferMap orgIdentifier={orgIdentifier} surface="home_open_houses" /> : (
            <div>
              <h2 className="text-2xl font-semibold" style={{ color: "var(--brand-text)" }}>Open Houses — Next 7 Days</h2>
              <div className="mt-6 flex min-h-[648px] flex-col items-center justify-center gap-4 rounded-xl"
                style={{ backgroundColor: "var(--brand-surface-subtle)", color: "var(--brand-text)" }}>
                <p role="status">{failed ? "Open houses could not load." : "Loading open houses…"}</p>
                {failed && <button type="button" onClick={() => setAttempt((value) => value + 1)} className="underline">Try again</button>}
                <Link href={`${basePath}/open-houses`} prefetch={false} className="underline">View all open houses</Link>
              </div>
            </div>
          )}
        </div>
      </Container>
    </section>
  );
}
